import * as React from 'react';
import { useAskExpert } from '../api/gateway-provider';
import { useGenerateTitleMutation } from './use-gateway-queries';
import { toast } from '../utils/toast';
import {
  GUEST_CHAT_LIMIT,
  useCanCreateGuestChat,
  useGuestActiveSession,
  useGuestActiveSessionId,
  useGuestChatCreationCount,
  useGuestChatStore,
  useGuestSessions,
} from '../stores/guest-chat-store';

export type UseGuestChatStateReturn = {
  sessions: ReturnType<typeof useGuestSessions>;
  activeSession: ReturnType<typeof useGuestActiveSession>;
  activeSessionId: string | null;
  chatCreationCount: number;
  canCreateChat: boolean;
  draftMessage: string;
  isLoading: boolean;
  newChat(): void;
  openSession(sessionId: string): void;
  deleteSession(sessionId: string): void;
  updateDraftMessage(message: string): void;
  submitMessage(directMessage?: string): Promise<void>;
};

export const useGuestChatState = (): UseGuestChatStateReturn => {
  const sessions = useGuestSessions();
  const activeSession = useGuestActiveSession();
  const activeSessionId = useGuestActiveSessionId();
  const chatCreationCount = useGuestChatCreationCount();
  const canCreateChat = useCanCreateGuestChat();

  const store = useGuestChatStore();
  const askExpertMutation = useAskExpert();
  const generateTitleMutation = useGenerateTitleMutation();

  const [draftMessage, setDraftMessage] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const newChat = React.useCallback(() => {
    if (!canCreateChat) {
      toast.error(
        'Chat limit reached',
        `Free plan allows ${GUEST_CHAT_LIMIT} chats. Sign in to unlock unlimited chats.`
      );
      return;
    }
    store.createSession();
    setDraftMessage('');
  }, [canCreateChat, store]);

  const openSession = React.useCallback(
    (sessionId: string) => {
      store.setActiveSession(sessionId);
      setDraftMessage('');
    },
    [store]
  );

  const deleteSession = React.useCallback(
    (sessionId: string) => {
      store.deleteSession(sessionId);
    },
    [store]
  );

  const updateDraftMessage = React.useCallback((message: string) => {
    setDraftMessage(message);
  }, []);

  const submitMessage = React.useCallback(
    async (directMessage?: string) => {
      const userInput = (directMessage ?? draftMessage).trim();
      if (!userInput || isLoading) return;

      let sessionId = activeSessionId;

      // Create session on first message if none is active
      if (!sessionId) {
        if (!canCreateChat) {
          toast.error(
            'Chat limit reached',
            `Free plan allows ${GUEST_CHAT_LIMIT} chats. Sign in to unlock unlimited chats.`
          );
          return;
        }
        sessionId = store.createSession();
        if (!sessionId) return;
      }

      setDraftMessage('');
      setIsLoading(true);

      const userMessageId = store.addMessage(sessionId, {
        content: userInput,
        sender: 'user',
        timestamp: new Date().toISOString(),
        status: 'sending',
      });

      const assistantMessageId = store.addMessage(sessionId, {
        content: '',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        status: 'processing',
      });

      // Generate title from first message (fire-and-forget)
      const currentSession = store.sessions.find(s => s.id === sessionId);
      const isFirstMessage = !currentSession || currentSession.messages.length <= 2;
      if (isFirstMessage) {
        generateTitleMutation
          .mutateAsync({ body: { firstMessage: userInput } })
          .then(result => {
            if (result.status === 200 && result.body?.title) {
              store.updateSessionTitle(sessionId!, result.body.title);
            }
          })
          .catch(() => {
            // Title generation is best-effort
          });
      }

      try {
        // Build chat history from existing messages (exclude the two we just added)
        const existingMessages = (currentSession?.messages ?? []).filter(
          m => m.id !== userMessageId && m.id !== assistantMessageId
        );
        const chatHistory = existingMessages.map(m => ({
          role: m.sender === 'user' ? ('user' as const) : ('assistant' as const),
          content: m.content,
        }));

        store.updateMessage(sessionId, userMessageId, { status: 'sent' });

        const result = await askExpertMutation.mutateAsync({
          body: {
            userInput,
            chatHistory,
          },
          headers: { 'x-stream': 'true' },
        });

        if (result.status !== 200) {
          const body = result.body as any;
          const errorMessage =
            typeof body?.error === 'string'
              ? body.error
              : typeof body?.message === 'string'
                ? body.message
                : `AI error: ${result.status}`;
          throw new Error(errorMessage);
        }

        if (!result.body) {
          throw new Error('No response body received');
        }

        const stream = result.body as ReadableStream;
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            store.updateMessage(sessionId, assistantMessageId, {
              status: 'sent',
              content: fullResponse,
            });
            break;
          }

          const textChunk = decoder.decode(value, { stream: true });
          buffer += textChunk;

          if (buffer.includes('event:')) {
            const lines = buffer.split('\n');
            buffer = '';

            let currentEvent = '';
            let currentData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                currentEvent = line.substring(7).trim();
              } else if (line.startsWith('data: ')) {
                currentData = line.substring(6).trim();
              } else if (line === '' && currentEvent && currentData) {
                if (currentEvent === 'status') {
                  try {
                    const statusData = JSON.parse(currentData);
                    if (!fullResponse) {
                      store.updateMessage(sessionId, assistantMessageId, {
                        content: `_${statusData.status}_`,
                        status: 'streaming',
                      });
                    }
                  } catch {}
                } else if (currentEvent === 'error') {
                  try {
                    const errorData = JSON.parse(currentData);
                    throw new Error(errorData.error || 'Stream error');
                  } catch (parseError) {
                    if (parseError instanceof SyntaxError) throw new Error(currentData);
                    throw parseError;
                  }
                }
                currentEvent = '';
                currentData = '';
              } else if (!line.startsWith('event:') && !line.startsWith('data:') && line.trim()) {
                buffer += line + '\n';
              }
            }
          } else {
            fullResponse += buffer;
            buffer = '';

            if (fullResponse.length === textChunk.length) {
              store.updateMessage(sessionId, assistantMessageId, { status: 'streaming' });
            }

            store.updateMessage(sessionId, assistantMessageId, {
              content: fullResponse,
              status: 'streaming',
            });
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred. Please try again.';

        store.removeMessage(sessionId, userMessageId);
        store.removeMessage(sessionId, assistantMessageId);

        toast.error('Chat Error', errorMessage);
        setDraftMessage(userInput);
      } finally {
        setIsLoading(false);
      }
    },
    [
      draftMessage,
      isLoading,
      activeSessionId,
      canCreateChat,
      store,
      askExpertMutation,
      generateTitleMutation,
    ]
  );

  return {
    sessions,
    activeSession,
    activeSessionId,
    chatCreationCount,
    canCreateChat,
    draftMessage,
    isLoading,
    newChat,
    openSession,
    deleteSession,
    updateDraftMessage,
    submitMessage,
  };
};
